import Sidebar from 'components/shared/sidebar';
import { useUI } from 'hooks';
import { useRouter } from 'next/router';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { sortMixedOptions } from 'lib/utils/optionsSorting';
import { Spinner } from 'components/shared/icons/ui';
import { trackEvents, trackInWindow } from 'hooks/tracking/track';
import Select from './filterSelects/select';
import Variant from './filterSelects/variant';
import ColorOptimisation from './filterSelects/color-optimisation';

const Price = dynamic(() => import('./filterSelects/price'));

const compareCount = ({ count: a }, { count: b }) => b - a;

export const tKey = key => {
  // Easier to read in Contentful
  if (key === 'f.specialfeaturesv2') return 'features';
  if (key === 'f.genericColorName') return 'color';
  if (key === 'f.genericFit') return 'fit';
  if (key === 'f.genericProductType') return 'category';
  return key.replace('f.', '');
};

// Sort option labels and return sorted option objects
const sortMixedOptionObjects = options =>
  sortMixedOptions(options.map(({ label }) => label))
    .map(label => options.find(option => label === option.label))
    .filter(Boolean);

const FilterSidebar = ({
  t,
  filters,
  filterValues,
  getFilterUrl,
  colorHexCodes,
  resetUrl,
  isAvailable,
  isLoading,
  // priceRange,
  // getRangeFilterUrl,
  // currency,
}) => {
  const { toggleFilter, displayFilter: show, isDesktop } = useUI();

  const { push } = useRouter();

  const [openFilter, setOpenFilters] = useState(null);
  const [openDesktopFilters, setOpenDesktopFilters] = useState([]);

  const toggleShow = () => {
    if (show) {
      // closing
      trackInWindow({
        event: trackEvents.applyFilter,
        gender: filterValues['f.gender'] ? filterValues['f.gender'].join(', ') : '',
        category: filterValues['f.genericProductType']
          ? filterValues['f.genericProductType'].join(', ')
          : '',
        jeans_type: filterValues['f.style'] ? filterValues['f.style'].join(', ') : '',
        features: filterValues['f.specialfeaturesv2']
          ? filterValues['f.specialfeaturesv2'].join(', ')
          : '',
        sizes: filterValues['f.size'] ? filterValues['f.size'].join(', ') : '',
        waist: filterValues['f.waist'] ? filterValues['f.waist'].join(', ') : '',
        length: filterValues['f.length'] ? filterValues['f.length'].join(', ') : '',
        colors: filterValues['f.genericColorName']
          ? filterValues['f.genericColorName'].join(', ')
          : '',
        fit: filterValues['f.genericFit'] ? filterValues['f.genericFit'].join(', ') : '',
        // price_min: filterValues['f.price'] ? filterValues['f.price'][0] : '',
        // price_max: filterValues['f.price'] ? filterValues['f.price'][1] : '',
      });
    }

    toggleFilter();
  };

  const toggleOpenFilter = key => {
    setOpenFilters(o => (o === key ? null : key));
    setOpenDesktopFilters(open =>
      open.includes(key) ? open.filter(k => k !== key) : [...open, key]
    );
  };

  const renderInput = filter => {
    const { key, values: options } = filter;
    if (key === 'f.genericColorName')
      return (
        <ColorOptimisation
          t={(optionKey, fallback) => t(`filter options - ${tKey(key)} - ${optionKey}`, fallback)}
          options={options.sort(compareCount)}
          colors={colorHexCodes}
          value={filterValues[key] || []}
          getFilterUrl={value => getFilterUrl(key, value)}
        />
      );

    // Commenting out as the price filter is not needed, but leaving in case it is needed in the future
    // if (key === 'f.price') {
    // const roundPriceRange = (price, i) => (i === 0 ? Math.floor(price) : Math.ceil(price));
    // const [min, initialMax] = priceRange.map(roundPriceRange);
    // Fix for 0-0 price ranges
    // const max = initialMax <= min ? min + 1 : initialMax;
    // const [minValue = min, maxValue = max] =
    //   filterValues[key]?.map(parseFloat).map(roundPriceRange) || [];

    // return (
    // <Price
    //   min={min}
    //   max={max}
    //   values={[minValue, maxValue]}
    //   onChange={v => push(getRangeFilterUrl(key, v), null, { shallow: true })}
    //   currency={currency}
    // />
    // );
    // }

    if (['f.waist', 'f.length', 'f.size'].includes(key))
      return (
        <Variant
          getFilterUrl={value => getFilterUrl(key, value)}
          value={filterValues[key] || []}
          options={sortMixedOptionObjects(options)}
          isAvailable={label => isAvailable(key, label)}
          filterKey={key}
        />
      );

    return (
      <Select
        t={optionKey => t(`filter options - ${tKey(key)} - ${optionKey}`, optionKey)}
        getFilterUrl={value => getFilterUrl(key, value)}
        options={options.sort(compareCount)}
        value={filterValues[key] || []}
        placeholder={t(`filter - ${tKey(key)}`, tKey(key))}
        filterKey={key}
      />
    );
  };

  return (
    <Sidebar isOpen={show} onClose={toggleShow}>
      <div className="flex h-full flex-col [&>*]:px-4 lg:[&>*]:px-9">
        <header className="flex items-center py-6 lg:py-8">
          <h2 className="text-heading-6"> {t('filter - filters', 'Filters')}</h2>
          <button type="button" className="text-body-2 ml-auto lg:hidden" onClick={toggleShow}>
            {t('filter - close', 'Close')}
          </button>
          <button
            type="button"
            className="text-body-2 ml-auto hidden lg:block"
            onClick={toggleShow}
          >
            {t('filter - close', 'Close')}
          </button>
        </header>
        <form className="flex h-full grow flex-col overflow-y-auto">
          {filters
            // Show f.size instead of f.waist, f.length, f.size
            .map(filter =>
              ['f.size', 'f.waist', 'f.length'].includes(filter.key)
                ? {
                    key: 'f.size',
                  }
                : filter
            )
            .filter(
              (filter, index, self) => self.findIndex(({ key }) => key === filter.key) === index // Unique by key
            )
            .map(filter => {
              return (
                <fieldset
                  key={filter.key}
                  className={`${openFilter && openFilter !== filter.key ? 'hidden lg:block' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleOpenFilter(filter.key)}
                    className="text-heading-7 relative flex w-full items-center gap-6 py-4"
                  >
                    {t(`filter - ${tKey(filter.key)}`, tKey(filter.key))}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`absolute right-0 h-[1.4em] transition-transform duration-300 ${
                        openFilter === filter.key ? 'rotate-180' : 'rotate-0'
                      } ${
                        openDesktopFilters.includes(filter.key) ? 'lg:rotate-180' : 'lg:rotate-0'
                      }`}
                    >
                      <path d="M3 5.5L8 10.5L13 5.5" stroke="#041C2C" />
                    </svg>
                  </button>
                  <div
                    className={`grid ${
                      filter.key !== 'f.price' && 'overflow-hidden'
                    } transition-[grid-template-rows] duration-300 ${
                      openFilter === filter.key ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    } ${
                      openDesktopFilters.includes(filter.key)
                        ? 'lg:grid-rows-[1fr]'
                        : 'lg:grid-rows-[0fr]'
                    }`}
                  >
                    <div
                      className={`min-h-0 transition-[visibility,opacity] duration-300 [&>*]:py-2 lg:[&>*]:py-4 ${
                        openFilter === filter.key ? 'visible opacity-100' : 'invisible opacity-0'
                      } ${
                        openDesktopFilters.includes(filter.key)
                          ? 'lg:visible lg:opacity-100'
                          : 'lg:invisible lg:opacity-0'
                      }`}
                    >
                      {filter.key === 'f.size' ? (
                        <div>
                          {['f.size', 'f.waist', 'f.length']
                            .map(key => filters.find(f => f.key === key))
                            .filter(Boolean)
                            .map(f => (
                              <div
                                key={f.key}
                                className="mb-7 border-b border-blue/10 pb-7 last:mb-0 last:border-none last:pb-0"
                              >
                                <p className="text-body-1 mb-2">
                                  {t(`filter - ${tKey(f.key)}`, tKey(f.key))}
                                </p>
                                {renderInput(f)}
                              </div>
                            ))}
                        </div>
                      ) : (
                        renderInput(filter)
                      )}
                    </div>
                  </div>
                </fieldset>
              );
            })}
          {/* Bottom border to indicate open filter on mobile */}
          {openFilter && (
            <div className="flex h-full grow flex-col justify-end pt-4">
              <hr className="block shrink-0 border-t border-blue/10 lg:hidden" />
            </div>
          )}
        </form>

        <footer className="flex gap-4 py-6 lg:py-8">
          {openFilter && !isDesktop ? (
            <button
              type="button"
              onClick={() => toggleOpenFilter(openFilter)}
              className="button w-full border border-blue bg-white text-blue"
            >
              {t('filter - back', 'Back')}
            </button>
          ) : (
            Object.keys(filterValues).length > 0 && (
              <Link href={resetUrl} shallow passHref>
                <a
                  role="button"
                  href="_"
                  onClick={toggleShow}
                  className="button w-full border border-blue bg-white text-blue"
                >
                  {t('filter - clearAll', 'Clear all')}
                </a>
              </Link>
            )
          )}
          <button type="button" onClick={toggleShow} className="button w-full">
            {t('filter - apply', 'Apply')}
          </button>
        </footer>
        {isLoading && (
          <aside
            aria-hidden
            className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-white bg-opacity-75"
          >
            <Spinner className="h-16 w-16" />
          </aside>
        )}
      </div>
    </Sidebar>
  );
};

export default FilterSidebar;
